import axios from 'axios'
import { subdomain } from 'utility/domain'
import { loadState, saveState } from 'utility/localStorage'
import { GET_USER_ME } from 'utility/endpoints'

export const UNAUTHORIZED_EVENT = 'auth:unauthorized'

let baseURL = '/'
const Axios = axios.create({
	baseURL,
	timeout: 100000000,
	crossdomain: true,
	withCredentials: false,
})

Axios.interceptors.request.use((config) => {
	const authToken = loadState('authToken')
	if (authToken) {
		config.headers['Auth-Token'] = authToken
	}

	if (subdomain) {
		config.headers['Tenant-Code'] = subdomain
	}

	return config
})

// A 401 from an arbitrary endpoint can be resource-level (a bad or missing id
// in the URL) rather than a dead session — wiping the token on the first one
// logs out users whose session is perfectly valid, and every request after
// that goes out with no Auth-Token at all. So before declaring the session
// dead, confirm it against the identity endpoint with the stored token.
let sessionCheckInFlight = null
const verifySession = () => {
	if (sessionCheckInFlight) return sessionCheckInFlight

	// If this 401s too, the interceptor below sees url === GET_USER_ME and
	// performs the actual logout — no recursion, since a tokenless retry
	// never carries Auth-Token and is ignored by the sentToken guard
	sessionCheckInFlight = Axios.get(GET_USER_ME)
		.catch(() => {})
		.finally(() => {
			sessionCheckInFlight = null
		})

	return sessionCheckInFlight
}

Axios.interceptors.response.use(
	(response) => response,
	(err) => {
		// Only treat it as a dead session when the request actually carried a
		// token — a 401 from a credentials-check (e.g. sign-in) must not redirect
		const sentToken = err?.config?.headers?.['Auth-Token']
		if (err?.response?.status === 401 && sentToken && typeof window !== 'undefined') {
			if (err?.config?.url === GET_USER_ME) {
				// The identity endpoint rejected the token — the session really is dead
				saveState('authToken', '')
				window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
			} else {
				// Could be a resource-level 401 — verify before logging the user out
				verifySession()
			}
		}
		return Promise.reject(err)
	},
)

export default Axios
